import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getSupplierInvitePreview, sendSupplierInvite, inviteAllSuppliers, formatApiError } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Copy, Check, Loader2, Send, Mail } from "lucide-react";

export const SupplierInviteDialog = ({ open, onOpenChange, supplierKey, supplierName, contactEmail, bulk = false, onSent }) => {
  const { user } = useApp();
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const key = bulk ? "your supplier" : supplierKey;
    const t = setTimeout(() => getSupplierInvitePreview(key, note).then(setPreview).catch((e) => toast.error(formatApiError(e?.response?.data?.detail))), 250);
    return () => clearTimeout(t);
  }, [open, note, supplierKey, bulk]);

  const copy = async () => {
    const body = `${preview.subject}\n\n${preview.text}`;
    try { await navigator.clipboard.writeText(body); } catch { window.prompt("Copy the email text:", body); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const send = async () => {
    setBusy(true);
    try {
      if (bulk) {
        const r = await inviteAllSuppliers(note, user);
        toast.success(r.sent ? `Invited ${r.sent} supplier${r.sent === 1 ? "" : "s"}` : "No suppliers left to invite — all with an email on file are already invited");
        if (r.failed?.length) toast.error(`${r.failed.length} could not be sent`);
      } else {
        const r = await sendSupplierInvite(supplierKey, note, user);
        toast.success(`Sent to ${r.to} — their emails are now trusted`);
      }
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="supplier-invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> {bulk ? "Invite all suppliers" : `Ask ${supplierName} to send invoices here`}</DialogTitle>
          <DialogDescription>
            {bulk ? "One email to every supplier with an address on file that hasn't been invited yet." : contactEmail ? <>Goes to <span className="font-mono text-foreground">{contactEmail}</span>.</> : "No email on file — copy the text and send it from your own mailbox."}
            {" "}Their address is trusted automatically, so future invoices skip the approval step.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea data-testid="invite-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} rows={2}
                    placeholder="Optional personal note, e.g. “Please use this from 1 July — our old accounts inbox is closing.”" />
          <div className="rounded-lg border border-border bg-muted/30 p-4 max-h-72 overflow-y-auto" data-testid="invite-preview">
            {!preview ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : (
              <>
                <div className="text-xs text-muted-foreground mb-2">Subject: <span className="text-foreground">{preview.subject}</span></div>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed" data-testid="invite-preview-text">{preview.text}</pre>
              </>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={copy} disabled={!preview} data-testid="invite-copy-btn">
            {copied ? <Check className="h-4 w-4 mr-1.5 text-emerald-500" /> : <Copy className="h-4 w-4 mr-1.5" />} {copied ? "Copied" : "Copy text"}
          </Button>
          <Button onClick={send} disabled={busy || (!bulk && !contactEmail)} data-testid="invite-send-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} {bulk ? "Send to all" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
